/**
 * O ESTOQUE DE RAÇÃO — as contas do galpão.
 *
 * Mora aqui, e não na tela, pelo motivo de sempre: é conta, e conta se testa.
 *
 * ── O TRATO BAIXA O ESTOQUE ───────────────────────────────────────────────
 *
 * Decisão do gestor em 22/09/2026, revendo a dele mesma. A anterior era que a
 * baixa fosse lançamento próprio, e a diferença entre o galpão e o trato
 * medisse o desperdício. Durou um dia.
 *
 * O que a derrubou não é o trabalho a mais: é o que o segundo registro mede.
 * Baixa digitada de memória no fim do dia, arredondada para saco inteiro, mede
 * o cuidado de quem digitou — não o desperdício. **Segundo registro ruim é
 * pior que nenhum**, porque produz alarme falso, e alarme falso ensina todo
 * mundo a ignorar o número.
 *
 * Quem soma o saldo é a view `v_estoque_racao` (migração 017), e não este
 * arquivo: uma definição só, para o banco inteiro. O que mora aqui é o que a
 * TELA precisa e o banco não dá — converter em sacos e estimar duração.
 */

/** O que o galpão tem de uma ração. Espelha `v_estoque_racao`. */
export interface SaldoDeRacao {
  racao_id: string
  granulometria_min_mm: number | null
  granulometria_max_mm: number | null
  proteina_pct: number | null
  /** O peso do saco em uso — o mesmo que o trato usa para converter. */
  peso_saco_kg: number
  saldo_kg: number
  /** Tudo que já foi comprado desta ração. Zero com saldo negativo = nunca. */
  entrou_kg: number
  /** Quilos que os tratos consumiram na janela curta, para leitura. */
  consumido_kg: number
}

/**
 * Quantos sacos são, no peso de saco em uso.
 *
 * Devolve nulo quando o peso do saco não é utilizável: melhor a tela dizer só
 * o quilo do que exibir um número de sacos vindo de divisão por zero.
 */
export function emSacosDe(quilos: number, pesoSacoKg: number): number | null {
  if (!Number.isFinite(quilos)) return null
  if (!Number.isFinite(pesoSacoKg) || pesoSacoKg <= 0) return null
  return quilos / pesoSacoKg
}

/**
 * Para quantos dias dá o que está no galpão, no ritmo dos últimos dias.
 *
 * ESTIMATIVA, e a tela tem de dizer isso (regra 8). O ritmo de uma semana não
 * prevê a próxima: o peixe cresce, a água muda, e um dia de chuva derruba a
 * oferta inteira. O número serve para uma pergunta só — «preciso comprar
 * ração esta semana?» — e para essa ele basta.
 *
 * Nulo quando não há ritmo para dividir: sem trato nenhum no período, não se
 * sabe se o estoque dura um dia ou um ano, e inventar um número seria pior
 * que não ter nenhum.
 */
export function diasDeRacao(saldoKg: number, ritmoKgDia: number): number | null {
  if (!Number.isFinite(saldoKg) || saldoKg < 0) return null
  if (!Number.isFinite(ritmoKgDia) || ritmoKgDia <= 0) return null
  return Math.floor(saldoKg / ritmoKgDia)
}

/**
 * POR QUE NÃO DÁ PARA ESTIMAR A DURAÇÃO.
 *
 * `diasDeRacao` devolve nulo por DOIS motivos diferentes, e a tela dizia
 * sempre o segundo: «sem trato nos últimos 7 dias». Com o galpão em −49.667 kg
 * e 17.588 kg tratados no mês, isso era simplesmente falso — e mandava
 * procurar um problema de trato que não existia, escondendo o de verdade, que
 * é a compra faltando.
 *
 * É a mesma armadilha do resto do projeto: uma defesa agiu e o aviso nomeou a
 * causa errada. Quem devolve nulo agora diz por quê, e a tela não tem como
 * escolher a frase errada. Mesmo padrão de `porQueNaoApura` na pesagem.
 */
export type SemPrevisao = 'saldo_negativo' | 'sem_ritmo'

export function porQueNaoDaParaEstimar(
  saldoKg: number,
  ritmoKgDia: number,
): SemPrevisao | null {
  // A ORDEM IMPORTA. Com o galpão negativo, o ritmo não interessa: mesmo
  // havendo trato todo dia, «dá para −300 dias» não quer dizer nada, e a
  // pergunta que falta responder é outra.
  if (!Number.isFinite(saldoKg) || saldoKg < 0) return 'saldo_negativo'
  if (!Number.isFinite(ritmoKgDia) || ritmoKgDia <= 0) return 'sem_ritmo'
  return null
}

/**
 * Saldo negativo é possível, e não se esconde.
 *
 * Com o trato baixando o estoque, é o caso mais comum do primeiro mês: o peixe
 * come todo dia e a compra ainda não foi lançada. Mostrar zero no lugar
 * apagaria justamente o sinal de que falta lançar a entrada.
 *
 * E há um segundo caso, que parece o mesmo e não é: o tratador escolheu no
 * trato uma especificação que nunca foi comprada — 6 a 8 mm com 36% quando o
 * galpão tem 32%. O saldo fica negativo numa linha e alto na outra, e o par
 * é o sintoma. Por isso a tela nunca soma as rações numa só.
 */
export function faltaLancarCompra(saldoKg: number): boolean {
  return Number.isFinite(saldoKg) && saldoKg < 0
}

/**
 * Nunca foi comprada, e o peixe comeu.
 *
 * Separado de `faltaLancarCompra` porque a frase muda: uma ração com compras
 * lançadas e saldo negativo pede UMA nota; uma que nunca teve entrada nenhuma
 * pede que alguém confira se a especificação escolhida no trato está certa.
 */
export function nuncaComprada(entrouKg: number): boolean {
  return !Number.isFinite(entrouKg) || entrouKg === 0
}

/**
 * O GALPÃO TEM A RAÇÃO QUE O TANQUE ESTÁ MARCADO PARA RECEBER?
 *
 * Pedido do gestor em 25/09/2026, aprovado no mesmo dia. O caso que o
 * originou: chegou a 8–10 · 28%, os tanques continuaram marcados com a 6–8 ·
 * 28%, e os tratos entraram com a especificação errada. O saldo da 6–8 foi
 * para o negativo, o da 8–10 ficou parado, e o erro só apareceu depois, no
 * Estoque — corrigido anulando trato por trato.
 *
 * O aviso chega ANTES do toque, na beira do tanque, e não trava nada: quem
 * está lá pode saber de uma pilha que o app não sabe. Por isso ele traz as
 * rações que o galpão TEM, prontas para trocar num toque.
 *
 * QUANDO O APP CALA:
 *   - o galpão não carregou (sem sinal): não se sabe nada, e aviso sobre o
 *     que não se sabe é alarme falso;
 *   - nenhuma compra foi lançada ainda: a fazenda não usa o estoque, e o
 *     aviso apareceria em todos os tanques, todo dia.
 *
 * O QUE ESTÁ NA FILA DO APARELHO CONTA, nos dois sentidos. A compra lançada
 * sem sinal é ração que chegou: o tratador que acabou de lançá-la não pode ler
 * «o galpão não tem» logo em seguida. E o trato lançado sem sinal é ração que
 * saiu: seis sacos ao longo do dia não podem deixar o aviso calado com o
 * galpão já vazio. É a armadilha de sempre deste app — o servidor não sabe o
 * que o aparelho ainda não mandou.
 */
export interface FaixaDeRacao {
  min_mm: number
  max_mm: number
  proteina_pct: number
}

/** O pedaço de `v_estoque_racao` que esta conta usa. */
export interface RacaoComSaldo {
  granulometria_min_mm: number | null
  granulometria_max_mm: number | null
  proteina_pct: number | null
  saldo_kg: number
  entrou_kg: number
}

/** Ração que a fila do aparelho põe (+) ou tira (−) do galpão. */
export interface MovimentoNaFila {
  faixa: FaixaDeRacao
  kg: number
}

/**
 * ABAIXO DISTO É ZERO. O saldo é soma de quilos com casa decimal, e a pilha
 * que acabou pode chegar como 0,0000000001 — que não é ração nenhuma. É a
 * mesma folga que o servidor usa para deixar ocultar uma ração zerada.
 */
const SALDO_ZERO_KG = 0.001

export type RespostaDoGalpao =
  | { tipo: 'nao_sei' }
  | { tipo: 'tem' }
  | { tipo: 'nao_tem'; outras: FaixaDeRacao[] }

function chave(f: FaixaDeRacao): string {
  return `${f.min_mm}|${f.max_mm}|${f.proteina_pct}`
}

/**
 * O que a fila do aparelho mexe no galpão, lido dos registros como eles são
 * gravados: compra é `mov_racao` com `tipo: 'entrada'`, perda é `mov_racao`
 * com `tipo: 'perda'`, e trato é `trato`. Recebe os registros e não a fila,
 * para o teste passar pelo MESMO filtro que a tela — a primeira versão
 * procurava `'compra'`, nome que nenhum registro tem, e o teste não via.
 */
export function movimentosDaFila(
  itens: { tipo: string; corpo: Record<string, unknown> }[],
  pesoSacoKg: number,
): MovimentoNaFila[] {
  const saida: MovimentoNaFila[] = []
  for (const item of itens) {
    const c = item.corpo
    let sinal = 0
    if (item.tipo === 'trato') sinal = -1
    else if (item.tipo === 'mov_racao' && c['tipo'] === 'entrada') sinal = 1
    else if (item.tipo === 'mov_racao' && c['tipo'] === 'perda') sinal = -1
    if (sinal === 0) continue

    const min = Number(c['granulometria_min_mm'])
    const max = Number(c['granulometria_max_mm'])
    const proteina = Number(c['proteina_pct'])
    const qtd = Number(c['quantidade_informada'])
    if (![min, max, proteina, qtd].every(Number.isFinite)) continue
    // Na COMPRA vale o saco daquela compra; no resto, a régua da fazenda.
    const sacoDaCompra = Number(c['peso_saco_kg'])
    const saco = sinal > 0 && Number.isFinite(sacoDaCompra) && sacoDaCompra > 0 ? sacoDaCompra : pesoSacoKg
    const kg = c['unidade_informada'] === 'saco' ? qtd * saco : qtd
    saida.push({ faixa: { min_mm: min, max_mm: max, proteina_pct: proteina }, kg: sinal * kg })
  }
  return saida
}

export function oGalpaoTem(
  marcada: FaixaDeRacao,
  racoes: RacaoComSaldo[] | null,
  fila: MovimentoNaFila[] = [],
): RespostaDoGalpao {
  if (racoes === null) return { tipo: 'nao_sei' }
  const algumaCompra = racoes.some((r) => r.entrou_kg > 0) || fila.some((m) => m.kg > 0)
  if (!algumaCompra) return { tipo: 'nao_sei' }

  // UMA LINHA POR ESPECIFICAÇÃO. O banco pode ter duas da mesma (uma oculta
  // com resíduo e a recomprada), e a fila soma por cima das duas.
  const saldo = new Map<string, { faixa: FaixaDeRacao; kg: number }>()
  const somar = (faixa: FaixaDeRacao, kg: number) => {
    const k = chave(faixa)
    const atual = saldo.get(k)
    if (atual) atual.kg += kg
    else saldo.set(k, { faixa, kg })
  }
  for (const r of racoes) {
    if (r.granulometria_min_mm === null || r.granulometria_max_mm === null) continue
    if (r.proteina_pct === null) continue
    somar(
      { min_mm: r.granulometria_min_mm, max_mm: r.granulometria_max_mm, proteina_pct: r.proteina_pct },
      r.saldo_kg,
    )
  }
  for (const m of fila) somar(m.faixa, m.kg)

  const daMarcada = saldo.get(chave(marcada))
  if (daMarcada && daMarcada.kg > SALDO_ZERO_KG) return { tipo: 'tem' }

  // A VIZINHA PRIMEIRO. O engano típico é a granulometria do lado, com a mesma
  // proteína — é ela que a pessoa procura na lista.
  const distancia = (f: FaixaDeRacao): number =>
    Math.abs(f.min_mm - marcada.min_mm) +
    Math.abs(f.max_mm - marcada.max_mm) +
    (f.proteina_pct === marcada.proteina_pct ? 0 : 100)
  const outras = [...saldo.values()]
    .filter((l) => l.kg > SALDO_ZERO_KG)
    .map((l) => l.faixa)
    .sort((a, b) => distancia(a) - distancia(b))
  return { tipo: 'nao_tem', outras }
}
